import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { LoginScreen, Topbar, Button } from '@neutheria/design-system';
import { meApi } from '../lib/api';
import { loadCache, saveCache, upsertProject, removeProject as removeCached, markDirty, isDirty } from '../lib/localCache';
import { importProjectExport, isProjectExport } from '../lib/importer';
export function Dashboard() {
    const navigate = useNavigate();
    const { isAuthenticated, isLoading, login, register, logout, user, getToken } = useKindeAuth();
    const [projects, setProjects] = useState([]);
    const [cached, setCached] = useState(() => loadCache());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showCreate, setShowCreate] = useState(false);
    const [newName, setNewName] = useState('');
    const [creating, setCreating] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [dirty, setDirty] = useState(() => isDirty());
    const [toast, setToast] = useState(null);
    const initialPullDone = useRef(false);
    const importInputRef = useRef(null);
    const getAccessToken = useCallback(async () => {
        if (!getToken)
            return null;
        const t = await getToken();
        return t ?? null;
    }, [getToken]);
    // Toast auto-dismiss
    useEffect(() => {
        if (!toast)
            return;
        const id = window.setTimeout(() => setToast(null), 3500);
        return () => window.clearTimeout(id);
    }, [toast]);
    const fetchProjects = useCallback(async () => {
        const token = await getAccessToken();
        if (!token)
            return;
        setLoading(true);
        setError(null);
        try {
            const list = await meApi.list(token);
            setProjects(list);
            // Seed the cache from the cloud on first successful pull
            const cache = list.map((p) => ({
                id: p.id,
                name: p.name,
                description: p.description,
                editSecret: p.editSecret,
                viewSecret: p.viewSecret,
                settings: p.settings,
                updatedAt: p.updatedAt,
            }));
            saveCache(cache);
            setCached(cache);
        }
        catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to load projects');
        }
        finally {
            setLoading(false);
        }
    }, [getAccessToken]);
    useEffect(() => {
        if (!isAuthenticated || initialPullDone.current)
            return;
        initialPullDone.current = true;
        fetchProjects();
    }, [isAuthenticated, fetchProjects]);
    const createProject = async () => {
        if (!newName.trim())
            return;
        const token = await getAccessToken();
        if (!token)
            return;
        setCreating(true);
        setError(null);
        try {
            const res = await meApi.create(token, newName.trim());
            setShowCreate(false);
            setNewName('');
            // Jump straight into the editor for the freshly-created project
            navigate(`/e/${res.id}/${res.editSecret}`);
        }
        catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to create project');
        }
        finally {
            setCreating(false);
        }
    };
    const deleteProject = async (p) => {
        if (!confirm(`Delete "${p.name}"? This can't be undone.`))
            return;
        const token = await getAccessToken();
        if (!token)
            return;
        try {
            await meApi.remove(token, p.id);
            removeCached(p.id);
            setProjects((list) => list.filter((x) => x.id !== p.id));
            setCached(loadCache());
        }
        catch (e) {
            setToast({ kind: 'error', message: e instanceof Error ? e.message : 'Delete failed' });
        }
    };
    const handleExportJson = () => {
        const cache = loadCache();
        const payload = {
            version: '1.0',
            kind: 'superflow.project-list',
            exportedAt: new Date().toISOString(),
            projects: cache,
        };
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const stamp = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 19);
        a.href = url;
        a.download = `superflow-projects-${stamp}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setToast({ kind: 'success', message: `Exported ${cache.length} project${cache.length === 1 ? '' : 's'}` });
    };
    const handleImportJson = async (file) => {
        try {
            const text = await file.text();
            const parsed = JSON.parse(text);
            // Shape 1: full single-project export (also covers legacy
            // `flowpath-export.json`). Rebuild it on the server and add to cache.
            if (isProjectExport(parsed)) {
                setToast({ kind: 'info', message: 'Importing project — rebuilding on server…' });
                const token = await getAccessToken();
                const imported = await importProjectExport(parsed, token);
                upsertProject({
                    id: imported.id,
                    name: imported.name,
                    editSecret: imported.editSecret,
                    viewSecret: imported.viewSecret,
                    updatedAt: new Date().toISOString(),
                });
                setCached(loadCache());
                if (token)
                    await fetchProjects();
                markDirty(true);
                setDirty(true);
                navigate(`/e/${imported.id}/${imported.editSecret}`);
                return;
            }
            // Shape 2: dashboard project-list export (`{ projects: [...] }` or a
            // bare array). Replace the local cache.
            const parsedObj = parsed;
            const list = Array.isArray(parsedObj)
                ? parsedObj
                : Array.isArray(parsedObj?.projects)
                    ? parsedObj.projects
                    : [];
            if (list.length === 0) {
                throw new Error('File does not contain any SuperFlow projects');
            }
            for (const p of list) {
                if (!p?.id || typeof p.id !== 'string')
                    throw new Error('Each project needs an id');
                if (!p?.name || typeof p.name !== 'string')
                    throw new Error('Each project needs a name');
            }
            saveCache(list);
            setCached(list);
            markDirty(true);
            setDirty(true);
            setToast({
                kind: 'success',
                message: `Imported ${list.length} project${list.length === 1 ? '' : 's'} — click Save to push to cloud`,
            });
        }
        catch (e) {
            setToast({ kind: 'error', message: e instanceof Error ? e.message : 'Failed to import' });
        }
    };
    const handleSync = async () => {
        const token = await getAccessToken();
        if (!token) {
            setToast({ kind: 'error', message: 'Sign in to sync to the cloud' });
            return;
        }
        setSyncing(true);
        try {
            const cache = loadCache();
            await meApi.sync(token, cache.map((p) => ({
                id: p.id,
                name: p.name,
                description: p.description,
                settings: p.settings,
                editSecret: p.editSecret,
                viewSecret: p.viewSecret,
            })));
            markDirty(false);
            setDirty(false);
            await fetchProjects();
            setToast({ kind: 'success', message: `Synced ${cache.length} project${cache.length === 1 ? '' : 's'} to cloud` });
        }
        catch (e) {
            setToast({ kind: 'error', message: e instanceof Error ? e.message : 'Sync failed' });
        }
        finally {
            setSyncing(false);
        }
    };
    // Combined view: show cloud projects if signed in, else the local cache.
    const visible = useMemo(() => {
        if (isAuthenticated)
            return projects;
        return cached.map((p) => ({
            id: p.id,
            userId: null,
            name: p.name,
            description: p.description,
            editSecret: p.editSecret,
            viewSecret: p.viewSecret,
            visibility: 'unlisted',
            settings: p.settings,
            createdAt: p.updatedAt,
            updatedAt: p.updatedAt,
            lastAccessedAt: p.updatedAt,
        }));
    }, [isAuthenticated, projects, cached]);
    // ── Loading ──
    if (isLoading) {
        return (_jsx("div", { className: "min-h-screen flex items-center justify-center", children: _jsxs("div", { className: "glass p-10 text-center", children: [_jsx("div", { className: "w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" }), _jsx("p", { className: "text-gray-500 text-sm", children: "Loading\u2026" })] }) }));
    }
    // ── Login ──
    if (!isAuthenticated) {
        return (_jsx(LoginScreen, { appName: "SuperFlow", tagline: "Visual timeline planning with parallel workstreams and conditional branching.", onLogin: () => login(), onRegister: () => register() }));
    }
    return (_jsxs("div", { className: "min-h-screen pb-12", children: [_jsx(Topbar, { appName: "SuperFlow", actions: _jsxs(_Fragment, { children: [_jsx(Button, { onClick: () => setShowCreate(true), variant: "primary", children: "+ New Project" }), _jsx("input", { ref: importInputRef, type: "file", accept: "application/json,.json", style: { display: 'none' }, onChange: (e) => {
                                const f = e.target.files?.[0];
                                if (f)
                                    handleImportJson(f);
                                e.target.value = '';
                            } }), _jsx(Button, { onClick: () => importInputRef.current?.click(), variant: "ghost", children: "Import" }), _jsx(Button, { onClick: handleExportJson, variant: "ghost", children: "Export" }), _jsxs(Button, { onClick: handleSync, disabled: syncing, variant: "secondary", children: [_jsx(SyncIcon, { spinning: syncing }), syncing ? 'Syncing…' : dirty ? 'Save' : 'Saved'] })] }), user: user ?? null, onLogout: logout }), _jsxs("div", { className: "max-w-5xl mx-auto px-3 sm:px-6 pt-6", children: [_jsxs("div", { className: "mb-8 text-center", children: [_jsx("h2", { className: "text-2xl sm:text-3xl font-semibold tracking-tight text-gray-900", children: "Your projects" }), _jsx("p", { className: "text-sm text-gray-500 mt-1", children: "Timelines, branches, and milestones \u2014 all in one canvas." })] }), error && (_jsxs("div", { className: "glass p-4 mb-4 text-sm text-red-600 flex items-center justify-between", children: [_jsx("span", { children: error }), _jsx("button", { className: "text-xs text-gray-400 hover:text-gray-600", onClick: () => setError(null), children: "Dismiss" })] })), loading && visible.length === 0 ? (_jsxs("div", { className: "glass p-12 text-center", children: [_jsx("div", { className: "w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" }), _jsx("p", { className: "text-gray-500 text-sm", children: "Loading projects\u2026" })] })) : visible.length === 0 ? (_jsxs("div", { className: "glass p-12 text-center", children: [_jsx("p", { className: "text-gray-700 text-base font-medium", children: "No projects yet" }), _jsx("p", { className: "text-gray-500 text-sm mt-1", children: "Create your first timeline to get started." }), _jsx("button", { className: "btn-primary mt-5", onClick: () => setShowCreate(true), children: "New Project" })] })) : (_jsx("div", { className: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5", children: visible.map((p) => {
                            const hasEdit = !!p.editSecret;
                            const href = hasEdit ? `/e/${p.id}/${p.editSecret}` : `/v/${p.id}/${p.viewSecret ?? ''}`;
                            return (_jsxs("div", { className: "relative group", children: [_jsxs("a", { href: href, className: "project-tile p-5 h-full flex flex-col", children: [_jsxs("div", { className: "flex items-center gap-3 mb-3", children: [_jsx("div", { className: "w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-500 flex items-center justify-center text-white text-sm font-bold", children: p.name.slice(0, 1).toUpperCase() }), _jsxs("div", { className: "min-w-0 flex-1", children: [_jsx("h3", { className: "text-base font-semibold text-gray-900 truncate", children: p.name }), p.description && (_jsx("p", { className: "text-xs text-gray-500 truncate", children: p.description }))] })] }), _jsxs("div", { className: "text-[11px] text-gray-400 mt-auto", children: ["Updated ", formatRelative(p.updatedAt)] })] }), _jsx("button", { className: "absolute top-3 right-3 w-8 h-8 rounded-lg bg-white/80 backdrop-blur text-gray-400 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center", onClick: (e) => { e.preventDefault(); deleteProject(p); }, title: "Delete", "aria-label": "Delete", children: _jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("path", { d: "M3 6h18" }), _jsx("path", { d: "M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" }), _jsx("path", { d: "M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" })] }) })] }, p.id));
                        }) }))] }), showCreate && (_jsxs("div", { className: "fixed inset-0 z-[100] flex items-center justify-center p-4", children: [_jsx("div", { className: "absolute inset-0 bg-black/30 backdrop-blur-sm", onClick: () => !creating && setShowCreate(false) }), _jsxs("div", { className: "modal-card relative w-full max-w-md p-6", children: [_jsx("h2", { className: "text-lg font-semibold text-gray-900 mb-5", children: "New Project" }), _jsx("label", { className: "text-xs text-gray-500 mb-1 block", children: "Name" }), _jsx("input", { autoFocus: true, className: "glass-input mb-5", placeholder: "Product launch 2026", value: newName, onChange: (e) => setNewName(e.target.value), onKeyDown: (e) => e.key === 'Enter' && !creating && createProject() }), _jsxs("div", { className: "flex gap-2", children: [_jsx("button", { className: "btn-primary flex-1 h-[48px] text-base", onClick: createProject, disabled: creating || !newName.trim(), children: creating ? 'Creating…' : 'Create Project' }), _jsx("button", { className: "h-[48px] px-4 text-sm text-gray-600 bg-white/60 hover:bg-white/90 border border-gray-200 rounded-xl transition-all font-medium", onClick: () => setShowCreate(false), disabled: creating, children: "Cancel" })] })] })] })), toast && _jsx("div", { className: 'toast ' + toast.kind, children: toast.message })] }));
}
function SyncIcon({ spinning }) {
    return (_jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", style: { animation: spinning ? 'spin 0.8s linear infinite' : undefined }, children: [_jsx("path", { d: "M21 12a9 9 0 1 1-3-6.7" }), _jsx("polyline", { points: "21 4 21 10 15 10" })] }));
}
function formatRelative(iso) {
    try {
        const diff = Date.now() - new Date(iso).getTime();
        const min = Math.floor(diff / 60_000);
        if (min < 1)
            return 'just now';
        if (min < 60)
            return `${min}m ago`;
        const h = Math.floor(min / 60);
        if (h < 24)
            return `${h}h ago`;
        const d = Math.floor(h / 24);
        if (d < 30)
            return `${d}d ago`;
        return new Date(iso).toLocaleDateString();
    }
    catch {
        return iso;
    }
}
